import * as React from "react";

import {
  BrowserRouter,
  RouteProps,
  StaticRouter,
  useLocation,
} from "react-router-dom";

import { App } from "./App";

import type { PageEnvelope } from "@skv/core";
import { AnalyticsProvider, nswTheme } from "@skv/core";

import { DefaultTheme, ThemeProvider } from "styled-components";

import {
  AppSettings,
  GlobalStaticProps,
  GlobalStaticProvider,
  OpportunitySearchQueryProvider,
  OrganisationSearchQueryProvider,
  PageEnvelopeProvider,
  ReduxProvider,
  RootViewProvider,
  ServerStaticProvider,
} from "@skv/state";

import {
  AuthenticatedUserState,
  EoiFormProvider,
  EoiFormState,
  UserProvider,
} from "~/state";

interface RootProps extends RouteProps {
  context: any; // ReactJS.NET -> ReactRouter
  pageEnvelope: PageEnvelope<unknown>;
  appSettings: AppSettings;
  global: GlobalStaticProps;
  user: AuthenticatedUserState;
  eoiForm: EoiFormState;
}

const RouterRootViewProvider: React.FC<{
  pageEnvelope: PageEnvelope<unknown>;
}> = ({ pageEnvelope, children }) => {
  const location = useLocation();

  return (
    <RootViewProvider
      initialState={{
        cache: {
          [`${location.pathname}${location.search}`]: pageEnvelope?.data,
        },
      }}
    >
      {children}
    </RootViewProvider>
  );
};

/* Server Entry, sorts out initial redux state */
const Root: React.FC<RootProps> = (props) => {
  const {
    location,
    context,
    pageEnvelope,
    global,
    appSettings,
    user,
    eoiForm,
  } = props;

  if (process.env.NODE_ENV === "development") {
    console.log(props);
    // eslint-disable-next-line
    React.useEffect(() => {
      window["__ROOT__"] = props;
    }, []);
  }

  const app = (
    <RouterRootViewProvider pageEnvelope={pageEnvelope}>
      <EoiFormProvider initialState={eoiForm || {}}>
        <GlobalStaticProvider value={global}>
          <AnalyticsProvider
            country="au"
            division="vol"
            site="nswgov"
            scriptSrc={appSettings.omnitureScriptUrl}
          >
            <ThemeProvider theme={nswTheme as DefaultTheme}>
              <ReduxProvider initialPageEnvelope={pageEnvelope}>
                <ServerStaticProvider appSettings={appSettings}>
                  <UserProvider user={user}>
                    <PageEnvelopeProvider initialPageEnvelope={pageEnvelope}>
                      <OpportunitySearchQueryProvider>
                        <OrganisationSearchQueryProvider>
                          <App />
                        </OrganisationSearchQueryProvider>
                      </OpportunitySearchQueryProvider>
                    </PageEnvelopeProvider>
                  </UserProvider>
                </ServerStaticProvider>
              </ReduxProvider>
            </ThemeProvider>
          </AnalyticsProvider>
        </GlobalStaticProvider>
      </EoiFormProvider>
    </RouterRootViewProvider>
  );

  if (typeof window === "undefined") {
    return (
      <StaticRouter context={context} location={location}>
        {app}
      </StaticRouter>
    );
  }

  return <BrowserRouter>{app}</BrowserRouter>;
};

export default Root;
